import React from "react";
import MarketingLayout, { PageMeta } from "@/components/MarketingLayout";

const updated = "9 August 2026";

function LegalPage({ title, intro, metaDescription, eyebrow = "Legal information", children }) {
  return (
    <MarketingLayout>
      <PageMeta title={`${title} | Nothing Before Law`} description={metaDescription} />
      <article className="mx-auto max-w-4xl px-4 py-14 sm:px-6 lg:px-8 lg:py-20">
        <header className="border-b border-[var(--m-border)] pb-10">
          <p className="marketing-eyebrow">{eyebrow}</p>
          <h1 className="mt-5 font-serif text-5xl leading-tight text-[var(--m-text)] sm:text-6xl">{title}</h1>
          <p className="mt-5 max-w-2xl text-lg leading-7 text-[var(--m-muted)]">{intro}</p>
          <p className="mt-5 text-sm text-[var(--m-muted)]">Last updated: {updated}</p>
        </header>
        <div className="marketing-legal-content">{children}</div>
      </article>
    </MarketingLayout>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2>{title}</h2>
      {children}
    </section>
  );
}

export function PrivacyPolicy() {
  return (
    <LegalPage
      title="Privacy Policy"
      intro="This policy explains what information the Case Diary handles, why it is used and the choices available to account holders."
      metaDescription="Privacy Policy for the Nothing Before Law Case Diary."
    >
      <Section title="1. Scope">
        <p>This policy applies to the Nothing Before Law website, Case Diary application and related support communications.</p>
        <p>Advocates and firms control the professional records they enter. They remain responsible for having a lawful basis to record client and matter information.</p>
      </Section>
      <Section title="2. Information we handle">
        <ul>
          <li>Account details such as name, email address, password hash, role and subscription status.</li>
          <li>Practice records such as matters, clients, hearings, tasks, documents, billing entries and firm settings.</li>
          <li>Security information such as session identifiers, PIN hashes, failed login counts and access timestamps.</li>
          <li>Support messages and technical information needed to diagnose an issue.</li>
        </ul>
      </Section>
      <Section title="3. How information is used">
        <ul>
          <li>Provide, secure and maintain the Case Diary.</li>
          <li>Authenticate users and enforce firm and role permissions.</li>
          <li>Generate documents, reminders and other features requested by the user.</li>
          <li>Process subscription requests and provide customer support.</li>
          <li>Prevent misuse, investigate errors and improve reliability.</li>
        </ul>
      </Section>
      <Section title="4. Sharing and service providers">
        <p>Information is not sold. It may be handled by hosting, email, notification or authentication providers only where needed to operate a requested feature.</p>
        <p>Information may also be disclosed when required by applicable law, a valid court order or to protect the security and rights of users and the service.</p>
      </Section>
      <Section title="5. Storage and security">
        <p>Reasonable technical and organisational safeguards are used, including password hashing, separate PIN protection, access controls and secure transport.</p>
        <p>No internet service can guarantee absolute security. Account holders should use strong credentials, protect their devices and promptly report suspected access.</p>
      </Section>
      <Section title="6. Retention and deletion">
        <p>Information is retained while an account is active and as reasonably needed for service, security, dispute resolution and legal obligations.</p>
        <p>Deletion requests may be subject to backup cycles, fraud prevention needs and records that must be retained by law or professional duty.</p>
      </Section>
      <Section title="7. Your choices">
        <p>Account holders may update profile and firm information in the application. Requests to access, correct or delete personal information can be sent to the contact below.</p>
        <p>Where a firm manages a team account, some requests may need to be handled by the firm's authorised administrator.</p>
      </Section>
      <Section title="8. Children">
        <p>The Case Diary is intended for legal professionals and is not directed to children.</p>
      </Section>
      <Section title="9. Changes to this policy">
        <p>This policy may be updated when the service, providers or legal requirements change. The revised date will be shown at the top of this page.</p>
      </Section>
      <Section title="10. Contact">
        <p>Privacy questions and requests may be sent to <a href="mailto:contact@nothingbeforelaw.com">contact@nothingbeforelaw.com</a>.</p>
      </Section>
    </LegalPage>
  );
}

export function TermsAndConditions() {
  return (
    <LegalPage
      title="Terms and Conditions"
      intro="These terms govern access to the Nothing Before Law website and Case Diary application."
      metaDescription="Terms and Conditions for the Nothing Before Law Case Diary."
    >
      <Section title="1. Acceptance">
        <p>By creating an account or using the Case Diary, you agree to these terms and the Privacy Policy. If you use the service for a firm, you confirm that you may bind that firm.</p>
      </Section>
      <Section title="2. The service">
        <p>The Case Diary provides tools for practice organisation, including matters, hearings, tasks, documents, billing and related workflows.</p>
        <p>The service is administrative software. It does not provide legal advice, represent any person or replace professional judgment and court procedure.</p>
      </Section>
      <Section title="3. Accounts and security">
        <ul>
          <li>Provide accurate account information and keep it current.</li>
          <li>Protect passwords, PINs, devices and account access.</li>
          <li>Use team roles only for authorised members of the practice.</li>
          <li>Notify support promptly if access may have been compromised.</li>
        </ul>
      </Section>
      <Section title="4. Professional responsibility">
        <p>You are responsible for verifying court dates, limitation periods, filings, calculations, generated documents and client communications before relying on them.</p>
        <p>You are also responsible for professional confidentiality, record-keeping duties and permissions relating to information entered into the service.</p>
      </Section>
      <Section title="5. Acceptable use">
        <p>Do not use the service to break the law, harm another person, gain unauthorised access, upload malicious material, overload the platform or infringe intellectual property or privacy rights.</p>
      </Section>
      <Section title="6. Plans and payments">
        <p>Paid features, prices and plan periods are shown before a subscription request is submitted. A manually reported UPI payment is activated only after its bank reference is reviewed and approved.</p>
        <p>Unless required by law or stated at purchase, fees for an active subscription period are non-refundable.</p>
      </Section>
      <Section title="7. Your content">
        <p>You retain rights in the information and documents you enter. You grant the service the limited permission needed to store, process, display and transmit that content for requested features.</p>
      </Section>
      <Section title="8. Availability and changes">
        <p>The service may be updated, suspended or changed to maintain security, reliability or functionality. Reasonable efforts will be made to avoid unnecessary interruption.</p>
      </Section>
      <Section title="9. Suspension and termination">
        <p>Access may be restricted for material breach, unlawful use, security risk or unpaid fees. You may stop using the service at any time.</p>
      </Section>
      <Section title="10. Disclaimers and liability">
        <p>The service is provided on an as-available basis to the extent permitted by law. Nothing Before Law does not guarantee that every feature or third-party integration will always be uninterrupted or error-free.</p>
        <p>To the extent permitted by law, liability is limited to direct losses caused by the service and excludes indirect, consequential or professional losses arising from unverified data or missed procedural obligations.</p>
      </Section>
      <Section title="11. Governing law">
        <p>These terms are governed by the laws of India. Courts with lawful jurisdiction in India may hear disputes that cannot be resolved directly.</p>
      </Section>
      <Section title="12. Contact">
        <p>Questions about these terms may be sent to <a href="mailto:contact@nothingbeforelaw.com">contact@nothingbeforelaw.com</a>.</p>
      </Section>
    </LegalPage>
  );
}

export function RefundPolicy() {
  return (
    <LegalPage
      title="Refund and Cancellation Policy"
      intro="This policy explains how subscription payments, cancellations and refunds are handled for the Case Diary."
      metaDescription="Refund and Cancellation Policy for the Nothing Before Law Case Diary."
    >
      <Section title="1. Subscriptions and billing">
        <p>Paid plans, their prices and their periods are shown clearly before a subscription request is submitted. Payments are made by UPI and a subscription is activated after its bank reference is reviewed and approved.</p>
      </Section>
      <Section title="2. Cancellation">
        <p>You may stop using the service or choose not to renew at any time. Cancelling does not delete your account; your data remains available under the free plan's limits and under the Privacy Policy.</p>
      </Section>
      <Section title="3. Refunds">
        <p>Unless required by law or stated at purchase, fees for an active subscription period are non-refundable once the plan has been activated.</p>
        <p>If a payment was made but the plan was not activated, or the same period was charged twice, write to us with the bank reference and the amount will be verified and refunded to the same account.</p>
      </Section>
      <Section title="4. How to raise a request">
        <p>Send refund or cancellation requests to <a href="mailto:contact@nothingbeforelaw.com">contact@nothingbeforelaw.com</a> from your registered email, with the payment date and bank reference. Requests are answered within 7 working days, and approved refunds are processed within 7–10 working days.</p>
      </Section>
    </LegalPage>
  );
}

export function Contact() {
  return (
    <LegalPage
      title="Contact Us"
      intro="Questions about the Case Diary, your subscription or your data — write to us and we will respond."
      metaDescription="Contact Nothing Before Law — support, subscription and data questions."
      eyebrow="Support"
    >
      <Section title="Email">
        <p>For all support, billing, privacy and legal queries: <a href="mailto:contact@nothingbeforelaw.com">contact@nothingbeforelaw.com</a>.</p>
        <p>Write from your registered email so your account can be verified. Most queries are answered within 2 working days.</p>
      </Section>
      <Section title="Grievances">
        <p>If a support reply has not resolved your concern, mark the email subject "Grievance" and it will be escalated and answered within 7 working days.</p>
      </Section>
      <Section title="Legal pages">
        <p>Before writing, the <a href="/privacy">Privacy Policy</a>, <a href="/terms">Terms and Conditions</a> and <a href="/refund-policy">Refund and Cancellation Policy</a> answer the most common questions.</p>
      </Section>
    </LegalPage>
  );
}
